import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// ──────────────────────────────────────────────────────────────────────
// PWA — register the service worker.
// Dev server uses Webpack HMR (incompatible with SW caching) so we only
// register in production builds. Behind a global try/catch — a missing
// SW is non-fatal, the app continues to work as a regular SPA.
//
// Iter-73: when a new SW version is detected, we immediately tell it to
// `SKIP_WAITING` and reload the page once it takes control. This is the
// rescue path for users stuck on v1's cache-first JS bundle that broke
// SVG animations.
// ──────────────────────────────────────────────────────────────────────
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Force an update check immediately on every page load — the
        // browser otherwise rechecks the SW only every 24h.
        reg.update().catch(() => {});

        // If a newer SW is already waiting, kick it live now.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }

        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) return;
          next.addEventListener("statechange", () => {
            // As soon as the new SW finishes installing, tell it to take
            // over without waiting for all tabs to close.
            if (next.state === "installed" && navigator.serviceWorker.controller) {
              next.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });

        // When the SW actually takes control (after skipWaiting), reload
        // once so the page is served by the new SW with fresh assets.
        let reloaded = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloaded) return;
          reloaded = true;
          window.location.reload();
        });
      })
      .catch((err) => console.warn("[SW] register failed (non-fatal)", err));
  });
}
